<template>
  <div class="context-menu-bar-imports">
    <menubar leftItemClass="pl-3" rightItemClass="pr-3">
      <template #leftItems>
        <b-dropdown toggle-class="mono" class="dropdown-default">
          <template #button-content
            ><span>{{ selectedImprotProjectName }}</span></template
          >
          <b-dropdown-item
            :active="importId === imp.importId"
            @click="handleImportProjectSelection(imp)"
            v-for="imp in sortedImportProjects"
            :key="imp.importId"
            >{{ imp.name }}
          </b-dropdown-item>
        </b-dropdown>
        <span v-tooltip="$t('ManageProjects')">
          <b-button class="btn-icon mono" @click="openManageImports" :disabled="showManageImports">
            <i class="mdp-Open"></i>
          </b-button>
        </span>
        <span v-tooltip="$t('DuplicateProject')">
          <b-button class="btn-icon mono" @click="copyImportProject">
            <i class="mdp-Duplicate"></i>
          </b-button>
        </span>
        <span v-tooltip="$t('new')">
          <b-button class="btn-icon mono" @click="addNewImport" :disabled="selectedImport?.importId == Guid.empty || showManageImports">
            <i class="mdp-Plus"></i>
          </b-button>
        </span>
        <span v-tooltip="$t('delete')" :disabled="showManageImports">
          <b-button class="btn-icon mono" @click="deleteImportProject" :disabled="selectedImport?.importId == Guid.empty || showManageImports">
            <i class="mdp-Delete"></i>
          </b-button>
        </span>
      </template>
      <template #rightItems>
        <b-button
          class="btn-with-icon mono"
          data-cy="last-import-btn"
          @click="goToLastImport()"
          :disabled="route.params.id === Guid.empty || !vm.hasPreviousImport">
          <span><i class="mdp-History"></i></span>
          <span>{{ $t("lastImport") }}</span>
        </b-button>
      </template>
    </menubar>
  </div>
</template>

<script setup lang="ts">
import { useRoute, useRouter } from "vue-router";
import menubar from "@common/components/MenuBar/MenuBar.vue";
import { ImportActions, ImportGetters, ImportProjectState, ImportState } from "@medapro-types/Imports/store/imports";
import { createNamespacedHelpers } from "vuex-composition-helpers";
import { Guid } from "@util/guid";
import { useI18n } from "vue-i18n";
import { computed, onMounted, reactive, watch } from "vue";
import { useImportMutations } from "../composables/useImportStore";
import { naturalSort } from "@util/naturalSort";
import { useToast } from "@common/composable/toast";
import importApi from "@services/imports";

//#region General
const router = useRouter();
const route = useRoute();
const { t: $t, te: $te } = useI18n();
const { createToast } = useToast();

const { useState: useImportState, useActions: useImportActions } = createNamespacedHelpers<ImportState, ImportGetters, ImportActions>("imports");
const { importProjects, selectedImport, importId, showManageImports } = useImportState(["importProjects", "selectedImport", "importId", "showManageImports"]);
const { deleteImport, copyImport } = useImportActions(["deleteImport", "copyImport"]);
const { "set-show-manage-imports": setShowManageImportStore } = useImportMutations();
//#endregion

//#region ViewModel
type ViewModel = {
  hasPreviousImport: boolean;
};
const vm = reactive<ViewModel>({ hasPreviousImport: false });
//#endregion

onMounted(async () => {
  await setPreviousImportState(route.params.id);
});

watch(
  () => route.params.id,
  async (nv) => {
    if (nv) {
      await setPreviousImportState(nv);
    }
  }
);

//#region Import General Functions
function handleImportProjectSelection(imp: ImportProjectState) {
  if (imp.importId !== route.params.id) {
    router.push({
      name: "create-import",
      params: { type: imp.type ?? route.params.type, id: imp.importId }
    });
  }
}

function addNewImport() {
  router.push({ name: "create-import", params: { type: route.params.type, id: Guid.empty } });
}

function openManageImports() {
  console.log("openManageImports");
  setShowManageImportStore(true);
}

async function setPreviousImportState(id: string) {
  vm.hasPreviousImport = await hasAnyLastImportRun(id);
}

async function copyImportProject() {
  try {
    if (importId.value) {
      const resp = await copyImport();
      handleImportProjectSelection(resp);
    }
  } catch (error: any) {
    if (error && error.isAxiosError) {
      if (error.response.status === 409) {
        createToast({
          text: $t("api.errors.Import." + error.response.data.error) ?? error.response.data.error,
          title: $t("Error"),
          variant: "danger",
          titleIcon: "Import",
          textIllustration: "Processing",
          statusTextIcon: "XCircleSolid"
        });
      }
    }
  }
}

const sortedImportProjects = computed(() => {
  return naturalSort(importProjects.value, (x) => x.name);
});

const selectedImprotProjectName = computed(() => {
  return importProjects.value.find((imp) => imp.importId === importId.value)?.name ?? importProjects.value[0]?.name;
});

async function deleteImportProject() {
  if (selectedImport.value == null) {
    return;
  }
  if (!confirm($t("Import.createImport.deleteImportConfirmation") as string)) {
    return;
  }
  try {
    if (selectedImport && selectedImport.value.importId !== Guid.empty) {
      await deleteImport([selectedImport.value.importId]);
      if (importId.value && !Guid.isEmpty(importId.value)) {
        router.replace({ name: "create-import", params: { type: route.params.type, id: importId.value } });
      }
    }
  } catch (error) {
    console.error("Error deleting import:", error);
  }
}

async function hasAnyLastImportRun(id: string) {
  if (!id || Guid.isEmpty(id)) {
    return false;
  }
  return await importApi.isLastImportEnabled(id);
}

async function goToLastImport() {
  try {
    const importRunId = await importApi.getLastImport(route.params.id);
    if (importRunId !== Guid.empty) router.push({ name: "progress-import", params: { type: route.params.type, id: importRunId } });
    else
      createToast({
        text: $t("api.errors.InvalidImportRun"),
        title: $t("Error"),
        variant: "danger",
        titleIcon: "Import",
        textIllustration: "History",
        statusTextIcon: "XCircleSolid"
      });
  } catch (error: any) {
    if (error && error.isAxiosError) {
      let errorMessage = "";
      if (error.response?.status === 409) {
        errorMessage = $t("api.errors.InvalidImport");
      } else {
        errorMessage = $te(`api.errors.${error?.response?.data.error}`) ? $t(`api.errors.${error?.response?.data.error}`) : error?.response?.data.error;
      }
      createToast({
        text: errorMessage,
        title: $t("Error"),
        variant: "danger",
        titleIcon: "Import",
        textIllustration: "History",
        statusTextIcon: "XCircleSolid"
      });
    }
  }
}

//#endregion
</script>
